import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

interface Toast {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  message: string;
  duration?: number;
  createdAt: number;
  action?: {
    label: string;
    onClick: () => void;
  };
}

export interface ToastOptions {
  action?: {
    label: string;
    onClick: () => void;
  };
}

interface UIState {
  // Sidebar
  sidebarOpen: boolean;
  sidebarCollapsed: boolean;

  // Theme
  theme: 'light' | 'dark' | 'system';

  // Toast notifications
  toasts: Toast[];
  lastToastTypeTimestamp: Record<string, number>;

  // Loading overlay
  globalLoading: boolean;
  loadingMessage: string | null;

  // Actions
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;
  toggleSidebarCollapse: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  setTheme: (theme: 'light' | 'dark' | 'system') => void;
  addToast: (toast: Omit<Toast, 'id' | 'createdAt'>) => void;
  removeToast: (id: string) => void;
  clearToasts: () => void;
  setGlobalLoading: (loading: boolean, message?: string | null) => void;
}


export const useUIStore = create<UIState>()(
  persist(
    (set) => ({
      // Initial state
      sidebarOpen: true,
      sidebarCollapsed: false,
      theme: 'light',
      toasts: [],
      lastToastTypeTimestamp: {},
      globalLoading: false,
      loadingMessage: null,

      // Actions
      toggleSidebar: () => set((state) => ({ sidebarOpen: !state.sidebarOpen })),
      setSidebarOpen: (open) => set({ sidebarOpen: open }),
      toggleSidebarCollapse: () => set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),
      setSidebarCollapsed: (collapsed) => set({ sidebarCollapsed: collapsed }),
      setTheme: (theme) => set({ theme }),

      addToast: (toast) => {
        set((state) => {
          const now = Date.now();
          const lastTime = state.lastToastTypeTimestamp[toast.type] || 0;
          if (now - lastTime < 3000) return state;
          return {
            toasts: [
              ...state.toasts,
              {
                ...toast,
                id: `toast-${now}-${Math.random().toString(36).substr(2, 9)}`,
                createdAt: now,
              },
            ],
            lastToastTypeTimestamp: {
              ...state.lastToastTypeTimestamp,
              [toast.type]: now,
            },
          };
        });
      },

      removeToast: (id) =>
        set((state) => ({
          toasts: state.toasts.filter((t) => t.id !== id),
        })),

      clearToasts: () => set({ toasts: [] }),

      setGlobalLoading: (loading, message = null) =>
        set({ globalLoading: loading, loadingMessage: message }),
    }),
    {
      name: 'entrybot-ui',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        sidebarCollapsed: state.sidebarCollapsed,
        theme: state.theme,
      }),
    }
  )
);

// Toast helper functions
export const showToast = {
  success: (message: string, duration?: number) => {
    useUIStore.getState().addToast({ type: 'success', message, duration });
  },
  error: (message: string, options?: ToastOptions) => {
    useUIStore.getState().addToast({ type: 'error', message, action: options?.action });
  },
  warning: (message: string, duration?: number) => {
    useUIStore.getState().addToast({ type: 'warning', message, duration });
  },
  info: (message: string, duration?: number) => {
    useUIStore.getState().addToast({ type: 'info', message, duration });
  },
};
